import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModalAction } from '../../../actions/modal';
import { updateAssets } from '../../../actions/inventory';
import moment from 'moment';
import { actualValueForDropdown } from '../../../utils/common';
import { validateStringValue } from '../../../utils/common';
import Form from '../../../common/Form';
import Input from '../../../common/Form/Input';
import Select from '../../../common/Form/Select';
import DatePicker from '../../../common/Form/DatePicker';
import { useForm, useFormContext } from 'react-hook-form';
import AssetType from '../../../components/Inputs/AssetType';
import { workingCondition } from '../../Inventory/reusableComponents/SpareAssetForm';

const formName = 'editInventoryAsset';

const warrantyType = [
    { key: 1, value: 'Manufacturing', label: 'Manufacturing' },
    { key: 2, value: 'Installation', label: 'Installation' },
    { key: 3, value: 'Billing', label: 'Billing' },
];

const status = [
    { key: 'AVAILABLE', label: 'Available', value: 'AVAILABLE' },
    { key: 'TRANSIT_IN', label: 'Transit In', value: 'TRANSIT_IN' },
    { key: 'TRANSIT_OUT', label: 'Transit Out', value: 'TRANSIT_OUT' },
    { key: 'DELIVERED', label: 'Delivered', value: 'DELIVERED' },
    { key: 'MISSING', label: 'Missing', value: 'MISSING' },
];


const EditInventoryAsset = ({ modalData, renderButtons }) => {
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const isAssetsTypeDropdownLoading = useSelector((state) => state.loading.isAssetsTypeDropdownLoading);

    const closeModal = () => {
        dispatch(closeModalAction());
    };

    const hasWarrantyDetails = (asset) => {
        if (validateStringValue(asset?.warranty_type) || moment(asset?.warranty_start_date).isValid() || moment(asset?.warranty_end_date).isValid()) return true;
        return false;
    };

    const defaultValues = {
        make: modalData.asset.make,
        model: modalData.asset.model,
        partcode: modalData.asset.partcode,
        serial_number: modalData.asset.serial_number,
        external_id: modalData.asset.external_id,
        warranty_start_date: modalData.asset.warranty_start_date ? new Date(modalData.asset.warranty_start_date) : '',
        warranty_end_date: modalData.asset.warranty_end_date ? new Date(modalData.asset.warranty_end_date) : '',
        warranty_type: warrantyType.find(type => type.value === modalData.asset.warranty_type),
        hasWarranty: hasWarrantyDetails(modalData.asset),
        working_condition: workingCondition.find(condition => condition.value === modalData.working_condition),
        status: status.find(s => s.value === modalData.status) || status[0],
        type: modalData.asset.type
    };


    useEffect(() => {
        if (modalData?.asset?.attributes?.length > 0) {
            modalData.asset.attributes.forEach(field => {
                if (typeof field.value === 'string' && typeof field.key === 'string') {
                    methods.setValue(field.key, field.value);
                }
            });
        }
    }, [modalData?.asset?.attributes]);

    const submit = (formValues) => {
        const payload = {
            serial_number: formValues.serial_number,
            asset_type_id: actualValueForDropdown(formValues.asset_type_id),
            ledger_id: modalData?.id,
            warranty_start_date: formValues.warranty_start_date ? moment(formValues.warranty_start_date).format('YYYY-MM-DD') : undefined,
            warranty_end_date: formValues.warranty_end_date ? moment(formValues.warranty_end_date).format('YYYY-MM-DD') : undefined,
            warranty_type: actualValueForDropdown(formValues.warranty_type) ? actualValueForDropdown(formValues.warranty_type).toUpperCase() : undefined,
            working_condition: actualValueForDropdown(formValues.working_condition),
            status: actualValueForDropdown(formValues.status),
            external_id: formValues.external_id || ''
        };

        let additional_fields = [];
        formValues.asset_type_id.fields.forEach(field => {
            const currentValue = formValues[field.key];
            const originalValue = modalData?.asset?.attributes?.find(attr => attr.key === field.key)?.value || '';

            if (typeof currentValue === 'string' && currentValue !== originalValue) {
                additional_fields.push({
                    key: field.key,
                    value: currentValue
                });
            }
        });

        payload.additional_fields = additional_fields;
        dispatch(updateAssets({ id: modalData.asset.id, payload, formName }))
    };

    const methods = useForm({ defaultValues, mode: 'all' });

    return (
        <>
            <Form
                className='w-full'
                onSubmit={submit}
                onCancel={closeModal}
                submitButtonText={'Update'}
                showSubmit
                submitContainerClassName='flex gap-2 justify-end py-4 px-4'
                isLoading={formButtonLoading[formName]}
                methods={methods}
                isSubmitDisabled={isAssetsTypeDropdownLoading}
            // renderButtons={renderButtons}
            >
                <EditAssetInputs modalData={modalData} />
            </Form>
        </>
    );
};

export default EditInventoryAsset;

const EditAssetInputs = ({ modalData }) => {
    const { watch } = useFormContext();
    const hasWarranty = watch('hasWarranty');

    const selectedAssetType = modalData.asset.asset_type;

    return (
        <div className='px-3 pt-3 overflow-y-auto  max-h-[calc(100vh-300px)]'>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                <div className='col-span-1 md:col-span-2 lg:col-span-1'>
                    <AssetType
                        name='asset_type_id'
                        label='Asset Type'
                        org_id={actualValueForDropdown(modalData.org_id)}
                        prefill={modalData.asset.asset_type_id}
                    />
                </div>

                {selectedAssetType && selectedAssetType.additional_fields.map((field, index) => (
                    <div key={index} className='col-span-1'>
                        <Input
                            label={field.label}
                            name={field.key}
                            type={'text'}
                            className="w-full"
                            required={field.is_required}
                        />
                    </div>
                ))}

                <div className='col-span-1'>
                    <Select label='Status' name='status' options={status} isClearable={false} />
                </div>
                <div className='col-span-1'>
                    <Select label='Working Condition' name='working_condition' options={workingCondition} isClearable={false} />
                </div>
                <div className='col-span-1'>
                    <Input name='external_id' label='External Id' type={'text'} />
                </div>

                {hasWarranty && (
                    <>
                        <div className='col-span-1'>
                            <Select label='Warranty Type' name='warranty_type' required options={warrantyType} shouldUnregister />
                        </div>
                        <div className='col-span-1'>
                            <DatePicker label='Warranty Start Date' name='warranty_start_date' />
                        </div>
                        <div className='col-span-1'>
                            <DatePicker label='Warranty End Date' name='warranty_end_date' shouldUnregister />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
